import {Button, CircularProgress, Grid, Tooltip} from '@material-ui/core';
import {ButtonProps} from '@material-ui/core/Button';
import {Info} from '@material-ui/icons';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncError, ServerError} from 'Core/models/Async';
import React, {ReactNode, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import 'Shared/components/FileDownloadButton.sass';
import {downloadFile, RequestMethod} from 'Shared/utilities/fetchHelpers';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {ReducerState} from 'rootReducer';

type FileDownloadButtonProps = {
  downloadedFileName: string;
  extension?: string;
  relativeFileLocation: string;
  children: ReactNode;
  className?: string;
  displayTooltip: boolean;
  tooltipMessage?: string;
  exportToCsvEvent?: string;
  requestMethod?: RequestMethod;
  requestPayload?: any;
  flag?: boolean;
  buttonFlag?: boolean;
  gridOverrideClassName?: string;
} & ButtonProps;

export enum FileExtension {
  XLSX = '.xlsx',
  TIFF = '.tiff',
  PDF = '.pdf',
  CSV = '.csv',
  XLS = '.xls',
}

const FileDownloadButton = (props: FileDownloadButtonProps) => {
  const {
    downloadedFileName,
    extension = '',
    relativeFileLocation,
    className,
    children,
    displayTooltip,
    tooltipMessage,
    requestMethod,
    requestPayload,
    buttonFlag,
    gridOverrideClassName = null,
    ...buttonProps
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const defaultTooltipMessage =
    (displayTooltip && !tooltipMessage) || (extension === FileExtension.CSV && !tooltipMessage && !props.flag)
      ? 'Only the first 5,000 records will be extracted'
      : tooltipMessage!;
  const buttonId = `file-download-button-${toKebabCase(downloadedFileName)}`;
  const handleButtonClick = () => {
    props?.exportToCsvEvent &&
      dispatch(
        changeAnalyticsState({
          page: analytics.page,
          ctaResponse: {eventAction: props.exportToCsvEvent},
        })
      );
    downloadFile(relativeFileLocation, `${downloadedFileName}${extension}`, undefined, requestMethod, requestPayload)
      .then((response: AsyncError | string) => {
        if (typeof response === 'string') {
          dispatch(createSnackbar(response));
        } else {
          if (response instanceof ServerError) {
            dispatch(createSnackbar('An error occurred while downloading the file.'));
          } else {
            dispatch(createSnackbar(response.message));
          }
        }
      })
      .then(() => {
        setIsLoading(false);
      });

    setIsLoading(true);
  };

  return (
    <Grid container item alignItems="center" className={gridOverrideClassName}>
      {isLoading ? (
        <Grid item>
          <CircularProgress className="file-download-button__loading" />
        </Grid>
      ) : (
        <>
          {displayTooltip && (
            <Tooltip title={defaultTooltipMessage} aria-label="info" tabIndex={0} aria-hidden={false}>
              <Info color="primary" />
            </Tooltip>
          )}
          <Grid item>
            <Tooltip title={defaultTooltipMessage} tabIndex={0} disableHoverListener={!displayTooltip}>
              <Button
                {...buttonProps}
                id={buttonProps.id ?? buttonId}
                className={className}
                color="primary"
                onClick={() => handleButtonClick()}
                style={{
                  backgroundColor: buttonFlag ? 'transparent' : '',
                  textDecoration: buttonFlag ? 'underline' : '',
                  padding: buttonFlag ? '0px' : '',
                  minWidth: '44px',
                  minHeight: '44px'
                }}
              >
                {children}
              </Button>
            </Tooltip>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default FileDownloadButton;
